<script setup lang="ts">
  import { computed, ref } from 'vue'
  import type {
    SuperUserOrganisation,
    SuperUserOrganisationUser
  } from '@/types/generated/types.gen'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Input } from '@/components/ui/input'
  import { ScrollArea } from '@/components/ui/scroll-area'
  import { Search, User, LogIn } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      title: 'Login As User',
      description: 'Select a user from {orgName} to login as.',
      searchPlaceholder: 'Search by name or email...',
      noUsersMatch: 'No users match your search.',
      noUsersFound: 'No users found for this organisation.',
      usersCount: '{count} of {total} users'
    },
    de: {
      title: 'Als Benutzer anmelden',
      description: 'Wählen Sie einen Benutzer von {orgName} aus, um sich anzumelden.',
      searchPlaceholder: 'Nach Name oder E-Mail suchen...',
      noUsersMatch: 'Keine Benutzer entsprechen Ihrer Suche.',
      noUsersFound: 'Keine Benutzer für diese Organisation gefunden.',
      usersCount: '{count} von {total} Benutzern'
    }
  })

  const props = defineProps<{
    organisation: SuperUserOrganisation | null
    open: boolean
  }>()

  const emit = defineEmits<{
    (e: 'update:open', value: boolean): void
  }>()

  const searchQuery = ref('')

  const filteredUsers = computed(() => {
    if (!props.organisation?.users) return []

    const query = searchQuery.value.toLowerCase().trim()
    if (!query) return props.organisation.users

    return props.organisation.users.filter((user) => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase()
      const email = user.email.toLowerCase()
      return fullName.includes(query) || email.includes(query)
    })
  })

  const handleLoginAs = (user: SuperUserOrganisationUser) => {
    // Redirect to Django login-as form
    // Using oid (organisation id / user pk) - need to get user pk
    // The current endpoint uses PK, but we have UUID
    // For now, we'll use the UUID-based endpoint that we'll add
    window.location.href = `/admin/loginas/user-login/uuid/${user.uuid}/`
  }

  const getInitials = (user: SuperUserOrganisationUser) => {
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
  }

  // Reset search when modal opens/closes
  const handleOpenChange = (value: boolean) => {
    if (!value) {
      searchQuery.value = ''
    }
    emit('update:open', value)
  }
</script>

<template>
  <Dialog
    :open="open"
    @update:open="handleOpenChange"
  >
    <DialogContent class="sm:max-w-[480px]">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <LogIn class="h-5 w-5" />
          {{ t('title') }}
        </DialogTitle>
        <DialogDescription>
          {{ t('description').replace('{orgName}', organisation?.name || '') }}
        </DialogDescription>
      </DialogHeader>

      <!-- Search Input -->
      <div class="relative">
        <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          v-model="searchQuery"
          :placeholder="t('searchPlaceholder')"
          class="pl-9"
        />
      </div>

      <!-- Users List -->
      <ScrollArea class="h-[320px] rounded-md border">
        <div class="p-2">
          <div
            v-if="filteredUsers.length === 0"
            class="flex flex-col items-center justify-center py-8 text-center"
          >
            <User class="mb-2 h-10 w-10 text-muted-foreground" />
            <p class="text-sm text-muted-foreground">
              {{ searchQuery ? t('noUsersMatch') : t('noUsersFound') }}
            </p>
          </div>

          <button
            v-for="user in filteredUsers"
            :key="user.uuid"
            class="flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors hover:bg-muted
              focus:bg-muted focus:outline-none"
            @click="handleLoginAs(user)"
          >
            <!-- Avatar -->
            <div
              v-if="user.avatar"
              class="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full"
            >
              <img
                :src="user.avatar"
                :alt="`${user.first_name} ${user.last_name}`"
                class="h-full w-full object-cover"
              />
            </div>
            <div
              v-else
              class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 text-sm
                font-medium text-purple-700"
            >
              {{ getInitials(user) }}
            </div>

            <!-- User Info -->
            <div class="min-w-0 flex-1">
              <p class="truncate font-medium text-foreground">
                {{ user.first_name }} {{ user.last_name }}
              </p>
              <p class="truncate text-sm text-muted-foreground">
                {{ user.email }}
              </p>
            </div>

            <!-- Arrow indicator -->
            <LogIn class="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          </button>
        </div>
      </ScrollArea>

      <!-- Footer info -->
      <p class="text-xs text-muted-foreground">
        {{
          t('usersCount')
            .replace('{count}', filteredUsers.length.toString())
            .replace('{total}', (organisation?.users?.length || 0).toString())
        }}
      </p>
    </DialogContent>
  </Dialog>
</template>
